import { CustomText, TableWrapper } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { moneyFormat } from '@4beefopag/4bee-fopag-core/dist/util';
import { CheckOutlined, CloseOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { faEllipsisH } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, Modal, Table, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import fileDownload from 'js-file-download';
import styled from 'styled-components';
import { useUserPolicy } from '../../../user-policy';
import { downloadDetailXlsxReport, process } from '../../../services/repository/benefitOrderRepository';
import mostrarErro from '../../../services/util/error-handler';
import { handleFileDownload } from '../../../services/util/file';
import { BENEFIT_ORDER } from '../../../services/util/globalConstant';
import BwgButton from '../../atoms/Button';
import BenefitOrderReopeningStatusTooltip from '../../molecules/BenefitOrderReopeningStatusTooltip';
import PageSizeOption from '../../molecules/PageSizeOption';

const Wrapper = styled(TableWrapper)`
  background-color: white;

  .ant-table-footer {
    padding: 0px;
    background: white;
  }
`;

function BenefitOrderTable({
  benefitOrders,
  onHandleChange,
  paginationData,
  currentSort,
  reloadOrders,
  onApproveReopeningClick,
  onDenyReopeningClick,
}) {
  const navigate = useNavigate();
  const { userAccount } = useUserPolicy();

  const getSortOrderByTitle = (title) => {
    if (currentSort?.column?.title === title) return currentSort.order;
    return null;
  };

  const confirmProcessing = async ({ company }) =>
    new Promise((res) => {
      Modal.confirm({
        title: 'Processar Pedido como Pago',
        content: (
          <div>
            <p>
              Ao confirmar, o Pedido de Vale da folha {company.name}, ela não poderá ser mais reaberta para alteração e
              todas as novas solicitações de reabertura/alteração de período de lançamento serão negados
              automaticamente.
            </p>
          </div>
        ),
        okText: 'Confirmar',
        cancelText: 'Cancelar',
        onOk: () => res(true),
        onCancel: () => res(false),
      });
    });

  const handleOnProcessClick = async (order) => {
    const shouldProceed = await confirmProcessing(order);
    if (!shouldProceed) {
      return;
    }

    try {
      await process(order.id);
      reloadOrders();
      Modal.success({
        title: 'Sucesso',
        content: <p>Pedido processado com sucesso!</p>,
      });
    } catch (e) {
      mostrarErro(e);
    }
  };

  const handleFileDownloadDetailXlsxReport = async (item) => {
    try {
      if (!item) return;
      const xlsx = await downloadDetailXlsxReport(item.id);
      fileDownload(xlsx, `Pedido de Vale ${item.id} ${item.company.name} ${item.competence.format('MM_YYYY')}.xlsx`);
    } catch (err) {
      mostrarErro(err);
    }
  };

  const createMenuItems = (order) => {
    const menuItems = [
      {
        label: (
          <span>
            <EyeOutlined /> &nbsp; Detalhes
          </span>
        ),
        onClick: () => navigate(`/benefits/${order.id}`),
      },
      {
        label: (
          <span>
            <DownloadOutlined /> &nbsp; Baixar Planilha
          </span>
        ),
        onClick: async () => handleFileDownload(() => handleFileDownloadDetailXlsxReport(order)),
      },
    ];

    if (userAccount.ability.can('process', 'benefit-order')) {
      menuItems.push({
        disabled: !order.canProcess,
        label: (
          <span>
            <CheckOutlined style={{ color: '#00a89c' }} /> &nbsp; Processar como pago
          </span>
        ),
        onClick: () => handleOnProcessClick(order),
      });
    }

    if (userAccount.ability.can('approve', 'benefit-order.reopening') && order.canApproveReopening) {
      menuItems.push(
        {
          label: (
            <span>
              <CheckOutlined style={{ color: '#00a89c' }} /> &nbsp; Aprovar Reabertura
            </span>
          ),
          onClick: () => onApproveReopeningClick(order),
        },
        {
          label: (
            <span>
              <CloseOutlined style={{ color: '#fa1664' }} /> &nbsp; Reprovar Reabertura
            </span>
          ),
          onClick: () => onDenyReopeningClick(order),
        }
      );
    }

    return menuItems;
  };

  const columns = [
    {
      title: 'Período',
      render: (_, item) => (item.isEditable ? 'Aberto' : 'Fechado'),
    },
    {
      title: 'Pedido',
      render: (_, item) => item.statusDescription,
    },
    {
      title: (
        <Tooltip title="Status da última solicitação">
          <span>Reabertura</span>
        </Tooltip>
      ),
      render: (_, order) => (
        <BenefitOrderReopeningStatusTooltip
          benefitOrder={order}
          placement="right"
        >
          <CustomText color={BENEFIT_ORDER.reopeningStatusColorMap[order.reopeningStatus]}>
            <b>{order.reopeningStatusDescription}</b>
          </CustomText>
        </BenefitOrderReopeningStatusTooltip>
      ),
    },
    {
      title: 'Competência',
      render: (_, item) => item.competence.format('MM/YYYY'),
      sorter: true,
      dataIndex: 'benefitOrder.competence',
      sortOrder: getSortOrderByTitle('Competência'),
    },
    {
      title: 'Folha',
      render: (_, item) => `<${item?.company?.id}> <${item?.company?.name}>`,
      sorter: true,
      dataIndex: 'company.name',
      sortOrder: getSortOrderByTitle('Folha'),
    },
    {
      title: 'Valor VA Total',
      render: (_, item) => moneyFormat(item.totalFoodVoucher || 0),
      sorter: true,
      dataIndex: 'benefitOrder.totalFoodVoucher',
      sortOrder: getSortOrderByTitle('Valor VA Total'),
    },
    {
      title: 'Valor VR Total',
      dataIndex: 'benefitOrder.totalMealVoucher',
      render: (_, item) => moneyFormat(item.totalMealVoucher || 0),
      sortOrder: getSortOrderByTitle('Valor VR Total'),
      sorter: true,
    },
    {
      title: '',
      render: (_, item) => (
        <Dropdown
          menu={{ items: createMenuItems(item) }}
          trigger={['click', 'hover']}
        >
          <BwgButton type="text">
            <FontAwesomeIcon
              icon={faEllipsisH}
              size="lg"
            />
          </BwgButton>
        </Dropdown>
      ),
    },
  ];

  return (
    <Wrapper>
      <PageSizeOption
        label="Exibir pedidos por página:"
        total={paginationData.total}
        pageSize={paginationData.pageSize}
        onChangePageSize={paginationData.updatePageSize}
      />
      <Table
        onChange={onHandleChange}
        columns={columns}
        dataSource={benefitOrders}
        pagination={{
          pageSize: paginationData.pageSize,
          total: paginationData.total,
          current: Number(paginationData.currentPage || 0),
        }}
      />
    </Wrapper>
  );
}

BenefitOrderTable.propTypes = {
  benefitOrders: PropTypes.arrayOf(),
  onHandleChange: PropTypes.func.isRequired,
  paginationData: PropTypes.shape(),
  currentSort: PropTypes.shape(),
  reloadOrders: PropTypes.func.isRequired,
  onApproveReopeningClick: PropTypes.func.isRequired,
  onDenyReopeningClick: PropTypes.func.isRequired,
};

BenefitOrderTable.defaultProps = {
  benefitOrders: [],
  paginationData: {},
  currentSort: {},
};

export default BenefitOrderTable;
